import {
    useFocusEffect,
    useNavigation,
    useRoute
} from '@react-navigation/native';
import {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react';
import {
    Platform,
    ScrollView
} from 'react-native';
import {
    useDispatch,
    useSelector
} from 'react-redux';

import { setFormData } from '../../actions/forms';
import { getConsentData } from '../../agreementsConfig';
import {
    BigButton,
    Button,
    ButtonsGroup,
    checkFormFields,
    Form,
    InfoList,
    Keyboard,
    Page,
    React,
    Section,
    SupportModal,
    TopNotification,
    View,
    withState
} from '../../common';
import { Log } from '../../common/Log';
import { Modal3 } from '../../common/Modal';
import {
    CHILD_HISTORY_KEY,
    STORE_PROFILE_CHILD
} from '../../hooks/usePersonalData';
import {
    getShowNewUserAgreementModal,
    getUserFetchingState,
    getUserInfo,
    getUserInfoFormData
} from '../../selectors';
import { getConsentFamily } from '../../selectors/consents';
import { getFormValues } from '../../selectors/forms';
import { formatFullDate } from '../../utils';
import {
    prepareNameForEdit,
    prepareNameForSave
} from '../../utils/strings';
import {
    ConsentConfirmationModal,
    getDoNotShowHistoryConsentWarning
} from '../Visits/EnrollVisit/ConsentConfirmationModal';
import { StyledAgreementCheckbox } from '../Visits/snippets/NewVisitModal/snippets.js';
import { getRelativesLimitsErrorTitle } from './utils';

const convertFormData = ({
    birthday,
    lastName,
    firstName,
    middleName,
    sex,
    email,
    profileId,
    phones,
    relKind,
    ...rest
}) => ({
    ...rest,
    date: formatFullDate(birthday),
    name: `${prepareNameForSave(lastName)} ${prepareNameForSave(firstName)} ${prepareNameForSave(middleName)}`,
    sex,
    email,
    profileId,
    phones,
    relKind,
    birthday,
    lastName: prepareNameForSave(lastName),
    firstName: prepareNameForSave(firstName),
    middleName: prepareNameForSave(middleName),
});

const EditConfirmationButtons = withState(
    ({
        onConfirm,
    }) => {
        return (
            <View>
                <ButtonsGroup style={{ paddingHorizontal: 0 }}>
                    <Button
                        normal
                        title="buttons.edit"
                        action={Page.closeModal}
                    />
                    <Button
                        primary
                        title="buttons.confirmed"
                        action={onConfirm}
                        //disabled={!childrenAgreement}
                    />
                </ButtonsGroup>
            </View>
        );
    },
);

const EditConfirmationButtonsForChild = ({
    onConfirm,
    /** When set, consent dialog is opened by parent via `modalStackLayer` (same native Modal — required on iOS). Receives `complete(s)` to run after user confirms. */
    onNeedsConsentConfirm,
    profileId,
}) => {
    const familyConsents = useSelector(getConsentFamily);
    const childConsents = familyConsents?.[profileId] || {};
    const mainConsents = childConsents?.main || {};

    const showStoreProfileChild = mainConsents[STORE_PROFILE_CHILD] !== true;
    const showChildHistory = mainConsents[CHILD_HISTORY_KEY] !== true;

    const [storeProfileChildChecked, setStoreProfileChildChecked] = useState(false);
    const [childHistoryChecked, setChildHistoryChecked] = useState(false);
    const [doNotShow, setDoNotShow] = useState(false);

    useFocusEffect(useCallback(() => {
        getDoNotShowHistoryConsentWarning().then((res) => {
            if (res) {
                setDoNotShow(true);
            }
        });
    }, []));

    const handleConfirm = useCallback(() => {
        const consents = {};
        if (showStoreProfileChild) {
            consents[STORE_PROFILE_CHILD] = storeProfileChildChecked;
        }
        if (showChildHistory) {
            consents[CHILD_HISTORY_KEY] = childHistoryChecked || false;
        }
        
        if (!showChildHistory || childHistoryChecked) {
            onConfirm(consents);
        } else if (doNotShow) {
            onConfirm(consents);
        } else if (onNeedsConsentConfirm) {
            onNeedsConsentConfirm((s) => {
                setDoNotShow(s);
                onConfirm(consents);
            });
        } else {
            Page.showSystemModal(<ConsentConfirmationModal
                onContinue={(s) => {
                    setDoNotShow(s);
                    onConfirm(consents);
                }}
                isSystemModal/>);
        }
    }, [childHistoryChecked,
        doNotShow,
        onConfirm,
        onNeedsConsentConfirm,
        storeProfileChildChecked,
        showStoreProfileChild,
        showChildHistory]);

    return (
        <View>
            {showStoreProfileChild && (
                <StyledAgreementCheckbox
                    titleComponent={getConsentData(STORE_PROFILE_CHILD)?.TextData}
                    selected={storeProfileChildChecked}
                    onPress={() => setStoreProfileChildChecked(!storeProfileChildChecked)}
                    containerStyle={{ marginTop: 10 }}
                    checkboxStyle={{ marginRight: 12 }}
                />
            )}
            {showChildHistory && (
                <StyledAgreementCheckbox
                    titleComponent={getConsentData(CHILD_HISTORY_KEY)?.TextData}
                    selected={childHistoryChecked}
                    onPress={() => setChildHistoryChecked(!childHistoryChecked)}
                    containerStyle={{ marginTop: 10 }}
                    checkboxStyle={{ marginRight: 12 }}
                />
            )}
            <ButtonsGroup style={{ paddingHorizontal: 0 }}>
                <Button
                    normal
                    title="buttons.edit"
                    action={Page.closeModal}
                />
                <Button
                    primary
                    title="buttons.confirmed"
                    action={handleConfirm}
                    disabled={showStoreProfileChild && !storeProfileChildChecked}
                />
            </ButtonsGroup>
        </View>
    );
};

/** Opens consent as a layer inside Modal3 — avoids a second RN Modal on iOS where it stays under the first. */
const AddChildProfileCheckModal = ({
    data,
    onSend,
    converter,
}) => {
    const [ consentOpen, setConsentOpen ] = useState(false);
    const consentCompleteRef = useRef(null);

    return (
        <Modal3
            title="titles.check_your_details"
            subtitle="titles.check_correctness"
            hideClose
            modalStackLayer={
                consentOpen ? (
                    <ConsentConfirmationModal
                        embedded
                        onDismiss={() => {
                            setConsentOpen(false);
                            consentCompleteRef.current = null;
                        }}
                        onContinue={(s) => {
                            const finish = consentCompleteRef.current;
                            consentCompleteRef.current = null;
                            setConsentOpen(false);
                            finish?.(s);
                        }}
                    />
                ) : null
            }
            showCross
            noScroll={false}
        >
            <View
                style={{
                    paddingBottom: 20,
                    paddingHorizontal: 12,
                }}
            >
                <InfoList
                    fields="userFields"
                    data={data}
                />
                <EditConfirmationButtonsForChild
                    profileId={data?.profileId}
                    onConfirm={(consents) =>
                        Page.closeModal(() => onSend({ ...converter(data), consents }))
                    }
                    onNeedsConsentConfirm={(complete) => {
                        consentCompleteRef.current = complete;
                        setConsentOpen(true);
                    }}
                />
            </View>
        </Modal3>
    );
};

const checkModalAction = (onSend, data, converter, formAction, filteredFields = []) => {
    const isChildProfile = data?.isChild || formAction === 'addChildren';
    if (isChildProfile && formAction === 'addChildren') {
        return Page.showModal(
            <AddChildProfileCheckModal
                converter={converter}
                data={data}
                onSend={onSend}
            />,
        );
    }
    return Page.showModal(
        <Modal3
            title="titles.check_your_details"
            subtitle="titles.check_correctness"
            hideClose
            showCross
            noScroll={false}
        >
            <View
                style={{
                    paddingBottom: 20,
                    paddingHorizontal: 12,
                }}
            >
                <InfoList
                    fields="userFields"
                    data={data}
                    filteredFields={filteredFields}
                />
                {isChildProfile ? (
                    <EditConfirmationButtonsForChild
                        profileId={data?.profileId}
                        onConfirm={(consents) =>
                            Page.closeModal(() => onSend({ ...converter(data), consents }))
                        }
                    />
                ) : (
                    <ButtonsGroup style={{ paddingHorizontal: 0 }}>
                        <Button
                            normal
                            title="buttons.edit"
                            action={Page.closeModal}
                        />
                        <Button
                            primary
                            title="buttons.confirmed"
                            action={() => Page.closeModal(() => onSend(converter(data)))}
                        //disabled={!childrenAgreement}
                        />
                    </ButtonsGroup>
                )}
            </View>
        </Modal3>,
    );
};
const useKeyboardHideCheck = (meta) => {
    const { params: { formData: lastFormData } = {} } = useRoute();
    const navigation = useNavigation();
    useEffect(() => {
        const onKeyboardHide = () => {
            // handler references values from when it was set - hence need to fetch fresh via 'getParam'
            if (lastFormData) {
                checkFormFields(lastFormData, meta, (delta) => navigation.setParams({ ...delta }));
            }
        };
        const keyboardListener = Keyboard.addListener('keyboardDidHide', onKeyboardHide);
        return () => {
            if (keyboardListener) {
                keyboardListener.remove();
            }
        };
    }, [ meta, lastFormData ]);
};

const EditPageComponent = ({
    meta,
    onSend,
    editProfileHint,
    sectionProps,
    withPage,
    hints,
    buttonTitle,
}) => {
    const userData = useSelector(getUserInfoFormData);
    const isUserFetching = useSelector(getUserFetchingState);
    const isEmailLogin = useSelector((state) => !!state.db?.isEmailLogin);
    const data = meta === 'addChild' || meta === 'addRelativeWithOtherInt' ? undefined : userData;
    const {
        me,
        isPerson,
    } = useSelector((state) => state?.user?.info) || {};
    const dispatch = useDispatch();
    const route = useRoute();
    const values = useSelector(getFormValues);
    const navigation = useNavigation();
    const showNewUserAgreementModal = useSelector(getShowNewUserAgreementModal);
    const backObject = {
        back: () => {
            navigation.goBack();
            return true;
        },
    };
    const formDataHeader = {
        EditRelative: backObject,
        EditChild: backObject,
        AddAdultRelative: backObject,
        EditAdultRelative: backObject,
    };

    useEffect(() => {
        dispatch(setFormData(formDataHeader));
    }, []);
    const {
        name,
        params: {
            fromScreen = 'Account',
            isFormValid,
            errors,
            relative,
            spouseError,
            parentError,
            action,
            data: formData = data ||
                (relative && {
                    birthday: Platform.OS === 'web' ? relative.birthday : relative?.birthday,
                    // ? new Date(relative.birthday)
                    // : undefined,
                    firstName: prepareNameForEdit(relative.firstName),
                    lastName: prepareNameForEdit(relative.surname),
                    middleName: prepareNameForEdit(relative.middleName),
                    sex: relative.sex,
                    profileId: relative.profileId,
                    phones: relative.phones,
                    relKind: relative.relKind,
                }),
        } = {},
    } = useRoute();
    const [ error, setError ] = useState();
    let errorMessage;
    if (spouseError || parentError) {
        errorMessage = getRelativesLimitsErrorTitle({
            spouseError,
            parentError,
            relKind: formData?.relKind,
        });
    }
    useKeyboardHideCheck(meta);
    const user = useSelector(getUserInfo);
    const isMainUserProfile = meta === 'editUserAccount';
    const formFilteredFields = isMainUserProfile
        ? [
            ...(userData?.isPhoneConfirmed ? [ 'phones' ] : []), ...(isEmailLogin ? [ 'email' ] : []),
        ]
        : (relative ? [ 'relKindWithOtherInt' ] : []);

    const footer = (
        <Section>
            <BigButton
                title={buttonTitle || 'buttons.further'}
                disabled={!isFormValid}
                busy={values?.pendingEditUserDP}
                action={() => {
                    if (errorMessage) {
                        SupportModal(errorMessage);
                    } else {
                        checkModalAction(
                            onSend?.(
                                fromScreen,
                                navigation,
                                Boolean(relative),
                                setError,
                                showNewUserAgreementModal ? !me?.consentReceived : false,
                                route?.params?.params,
                                user,
                            ),
                            {
                                ...formData,
                                isPerson,
                                // isChild: name === 'EditRelative',
                                isChild: !!relative?.isChild,
                                relative,
                            },
                            convertFormData,
                            // !!me?.consentReceived,
                            // showNewUserAgreementModal,
                            action,
                            formFilteredFields,
                        );
                    }
                }}
            />
        </Section>
    );
    const child = (
        <>
            <ScrollView
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator={false}
                style={{ flex: 1 }}
                automaticallyAdjustKeyboardInsets={Platform.OS === 'ios'}
                keyboardDismissMode="interactive"
            >
                <TopNotification hint={editProfileHint}/>

                <Section
                    {...sectionProps}
                    textStyles={{
                        color: '#575757',
                        paddingLeft: 4,
                    }}
                >
                    <Log
                        value={{formData}}
                        display={false}
                        text={'Form data'} />
                    <Form
                        data={formData}
                        meta={meta}
                        errors={errors}
                        checkValidation={(delta) => navigation.setParams({ ...delta })}
                        onChange={(delta) =>
                            navigation.setParams({
                                ...delta,
                                formData: delta.data,
                            })
                        }
                        hints={hints}
                        filteredFields={formFilteredFields}
                        readOnlyFields={relative && [ 'phones', 'relKind' ]}
                    />

                </Section>
            </ScrollView>
        </>
    );
    return withPage ? (
        <Page
            name="edit-account"
            notification={error}
            footer={footer}
            safeArea
            noScroll
            enableAutoHideKeyboard={Platform.OS !== 'ios'}
            isLoading={isUserFetching}
        >
            {child}
        </Page>
    ) : (
        <>
            {child}
            {footer}
        </>
    );
};

export const EditPage = (
    onSend,
    meta,
    editProfileHint = 'hints.edit_profile',
    formTitle,
    {
        withPage = true,
        buttonTitle,
    } = {
        withPage: true,
        buttonTitle: undefined,
    },
) => {

    const sectionProps = formTitle ? { title: formTitle } : {};
    const hints = {};
    return () => (
        <EditPageComponent
            meta={meta}
            hints={hints}
            editProfileHint={editProfileHint}
            withPage={withPage}
            onSend={onSend}
            sectionProps={sectionProps}
            buttonTitle={buttonTitle}
        />
    );
};
